import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { useAuthStore } from "../stores/authStore";
import { useFamilyStore } from "../stores/familyStore";
import ParentSidebar from "../components/ParentSidebar";
import ConfirmModal from "../components/ConfirmModal";
import type { Room } from "@starquest/core";
import styles from "./HouseBuilder.module.css";

const ROOM_PALETTE = [
  { emoji:"🛏", name:"Bedroom" }, { emoji:"🛁", name:"Bathroom" }, { emoji:"🍳", name:"Kitchen" },
  { emoji:"🛋️", name:"Living Room" }, { emoji:"📖", name:"Study" }, { emoji:"🧺", name:"Laundry" },
  { emoji:"🚪", name:"Entry" }, { emoji:"🏃", name:"Hallway" }, { emoji:"🗄", name:"Closet" },
  { emoji:"🌳", name:"Garden" }, { emoji:"🚗", name:"Garage" }, { emoji:"📦", name:"Attic" },
];

const FLOORS = [
  { id:"outdoor", label:"🌳 Yard",     sub:"OUTDOOR" },
  { id:"ground",  label:"🍳 1st Floor", sub:"GROUND" },
  { id:"upper",   label:"🛏 2nd Floor", sub:"BEDROOMS" },
  { id:"attic",   label:"📦 Attic",    sub:"SECRET" },
];

export default function HouseBuilder() {
  const navigate = useNavigate();
  const { familyId } = useAuthStore();
  const { rooms, setRooms } = useFamilyStore();

  const [floor,    setFloor]   = useState<string>("ground");
  const [selected, setSelected]= useState<string | null>(null);
  const [saving,   setSaving]  = useState(false);
  const [drawMode, setDrawMode] = useState(false);
  const [drawPos,  setDrawPos]  = useState<{x:number;y:number} | null>(null);
  const [drawPick,      setDrawPick]      = useState(false);
  const [confirmDelRoom, setConfirmDelRoom] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!familyId) return;
    api.listRooms(familyId).then((r) => setRooms(r as Room[]));
  }, [familyId]);

  const floorRooms = rooms.filter((r) => r.floor === floor);
  const selectedRoom = rooms.find((r) => r.roomId === selected);

  function addRoom(emoji: string, name: string) {
    const newRoom: Room = {
      roomId: crypto.randomUUID(),
      familyId: familyId!,
      name, emoji, floor: floor as Room["floor"],
      isSecret: floor === "attic",
      isVisible: true,
      position: { x: 10, y: 10, w: 30, h: 30 },
      createdAt: new Date().toISOString(),
    };
    setRooms([...rooms, newRoom]);
    setSelected(newRoom.roomId);
  }

  function updateRoom(id: string, patch: Partial<Room>) {
    setRooms(rooms.map((r) => r.roomId === id ? { ...r, ...patch } : r));
  }

  function deleteRoom(id: string) {
    setConfirmDelRoom(id);
  }

  function confirmDeleteRoom() {
    if (!confirmDelRoom) return;
    setRooms(rooms.filter((r) => r.roomId !== confirmDelRoom));
    setSelected(null);
    setConfirmDelRoom(null);
  }

  async function save() {
    if (!familyId) return;
    setSaving(true);
    await api.saveRooms(familyId, rooms);
    setSaving(false);
    navigate("/parent");
  }

  // Drag rooms on canvas
  function onPointerDown(e: React.PointerEvent, roomId: string) {
    e.currentTarget.setPointerCapture(e.pointerId);
    const canvas = canvasRef.current!.getBoundingClientRect();
    const startX = e.clientX; const startY = e.clientY;
    const room = rooms.find((r) => r.roomId === roomId)!;
    const origX = room.position.x; const origY = room.position.y;

    function onMove(me: PointerEvent) {
      const dx = ((me.clientX - startX) / canvas.width)  * 100;
      const dy = ((me.clientY - startY) / canvas.height) * 100;
      const newX = Math.max(0, Math.min(100 - room.position.w, origX + dx));
      const newY = Math.max(0, Math.min(100 - room.position.h, origY + dy));
      updateRoom(roomId, { position: { ...room.position, x: newX, y: newY } });
    }
    function onUp() { document.removeEventListener("pointermove", onMove); document.removeEventListener("pointerup", onUp); }
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  }

  return (
    <div className={styles.page}>
      <ParentSidebar />

      <div className={styles.body}>
        {/* Top bar */}
        <div className={styles.topBar}>
          <button className={styles.back} onClick={() => navigate("/parent")}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18 L9 12 L15 6"/></svg>
          </button>
          <h1 className={styles.title}>🏗️ House Builder</h1>
          <span className={styles.titleSub}>Sketch your home — the rooms kids see in their map</span>
          <div className={styles.spacer} />
          <div className={styles.toolGrp}>
            <button className={`${styles.tool} ${!drawMode ? styles.toolActive : ""}`}
              onClick={() => setDrawMode(false)}>Select</button>
            <button className={`${styles.tool} ${drawMode ? styles.toolActive : ""}`}
              onClick={() => setDrawMode(true)}>Draw Room</button>
          </div>
          <button className={styles.preview} onClick={async () => {
            if (!familyId) return;
            await api.saveRooms(familyId, rooms);
            navigate("/kid");
          }}>🏠 Preview as Kid</button>
          <button className={styles.save} onClick={save} disabled={saving}>
            {saving ? "Saving…" : "💾 Save"}
          </button>
        </div>

        <div className={styles.content}>
          {/* Floors */}
          <div className={styles.floors}>
            <div className={styles.flLabel}>Floors</div>
            {FLOORS.map((f) => (
              <button key={f.id} className={`${styles.flTab} ${floor === f.id ? styles.flActive : ""}`}
                onClick={() => setFloor(f.id)}>
                <div>
                  <div>{f.label}</div>
                  <div className={styles.flSub}>{f.sub}</div>
                </div>
                <div className={styles.flCount}>{rooms.filter((r) => r.floor === f.id).length}</div>
              </button>
            ))}
            <div className={styles.floorNote}>4 floors pre-configured</div>
          </div>

          {/* Canvas */}
          <div className={styles.canvasWrap}>
            {drawPick && (
              <div style={{position:"fixed",inset:0,zIndex:9}} onClick={() => { setDrawPick(false); setDrawPos(null); }} />
            )}
            <div className={styles.canvas} ref={canvasRef}
              onClick={(e) => {
                if (!drawMode) return;
                const rect = canvasRef.current!.getBoundingClientRect();
                const x = ((e.clientX - rect.left) / rect.width) * 100;
                const y = ((e.clientY - rect.top) / rect.height) * 100;
                setDrawPos({ x: Math.min(x, 70), y: Math.min(y, 70) });
                setDrawPick(true);
              }}
            >
              <div className={styles.canvasTitle}>
                <span>{FLOORS.find((f) => f.id === floor)?.label ?? "Floor"}</span>
                <span className={styles.canvasSub}>· {floorRooms.length} rooms</span>
              </div>

              {floorRooms.length === 0 && !drawPick && (
                <div className={styles.canvasHint}>
                  <div className={styles.canvasHintIcon}>🏠</div>
                  <div className={styles.canvasHintTitle}>This floor is empty</div>
                  <div className={styles.canvasHintSub}>
                    Tap <strong>Draw Room</strong> above, then click anywhere on this canvas to place a room
                  </div>
                </div>
              )}

              {floorRooms.map((r) => (
                <div
                  key={r.roomId}
                  className={`${styles.roomShape} ${r.isSecret ? styles.secret : ""} ${selected === r.roomId ? styles.roomSelected : ""}`}
                  style={{ left:`${r.position.x}%`, top:`${r.position.y}%`, width:`${r.position.w}%`, height:`${r.position.h}%` }}
                  onClick={() => setSelected(r.roomId)}
                  onPointerDown={(e) => onPointerDown(e, r.roomId)}
                >
                  <div className={styles.roomEmoji}>{r.emoji}</div>
                  <div className={styles.roomName}>{r.name}</div>
                </div>
              ))}

              <div className={styles.guide}>DRAG TO MOVE</div>
            </div>

            {drawPick && drawPos && (
              <div style={{
                position:"absolute",
                left: `${Math.min(drawPos.x + 5, 60)}%`,
                top: `${Math.min(drawPos.y + 5, 60)}%`,
                background:"#1a1a35",
                border:"1px solid rgba(255,215,0,0.4)",
                borderRadius:16,
                padding:16,
                zIndex:10,
                boxShadow:"0 8px 32px rgba(0,0,0,0.6)",
                minWidth:200,
              }}>
                <div style={{fontFamily:"var(--font-display)",fontWeight:900,fontSize:13,marginBottom:10,color:"var(--gold)"}}>Pick a room</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:6}}>
                  {ROOM_PALETTE.map((p) => (
                    <button key={p.name} style={{
                      padding:"8px 4px",background:"rgba(255,255,255,0.06)",
                      border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,
                      display:"flex",flexDirection:"column",alignItems:"center",gap:2,
                      cursor:"pointer",color:"var(--star)",
                    }}
                      onClick={() => {
                        const newRoom: Room = {
                          roomId: crypto.randomUUID(),
                          familyId: familyId!,
                          name: p.name, emoji: p.emoji,
                          floor: floor as Room["floor"],
                          isSecret: floor === "attic",
                          isVisible: true,
                          position: { x: drawPos.x, y: drawPos.y, w: 25, h: 25 },
                          createdAt: new Date().toISOString(),
                        };
                        setRooms([...rooms, newRoom]);
                        setSelected(newRoom.roomId);
                        setDrawPick(false); setDrawPos(null); setDrawMode(false);
                      }}>
                      <span style={{fontSize:18}}>{p.emoji}</span>
                      <span style={{fontSize:8,fontWeight:700,textTransform:"uppercase",opacity:.7}}>{p.name}</span>
                    </button>
                  ))}
                </div>
                <button style={{marginTop:10,width:"100%",padding:"6px 0",background:"transparent",border:"1px solid rgba(255,255,255,0.12)",borderRadius:8,color:"rgba(240,240,255,0.6)",fontSize:11,cursor:"pointer"}}
                  onClick={() => { setDrawPick(false); setDrawPos(null); }}>Cancel</button>
              </div>
            )}
          </div>

          {/* Right panel */}
          <div className={styles.rightPanel}>
            <div>
              <h3 className={styles.panelTitle}>Room Library</h3>
              <div className={styles.palette}>
                {ROOM_PALETTE.map((p) => (
                  <button key={p.name} className={styles.palBtn} onClick={() => addRoom(p.emoji, p.name)}>
                    <span className={styles.palEmoji}>{p.emoji}</span>
                    <span className={styles.palName}>{p.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {selectedRoom && (
              <div className={styles.propPanel}>
                <div className={styles.propHead}>
                  <div className={styles.propEmoji}>{selectedRoom.emoji}</div>
                  <div>
                    <div className={styles.propTitle}>{selectedRoom.name}</div>
                    <div className={styles.propSub}>{floor.toUpperCase()} · SELECTED</div>
                  </div>
                </div>
                <div className={styles.propRow}>
                  <label>Display name</label>
                  <input value={selectedRoom.name}
                    onChange={(e) => updateRoom(selectedRoom.roomId, { name: e.target.value })} />
                </div>
                <div className={styles.propRow}>
                  <label>Visible to kids</label>
                  <button
                    className={`${styles.tog} ${selectedRoom.isVisible ? styles.togOn : ""}`}
                    onClick={() => updateRoom(selectedRoom.roomId, { isVisible: !selectedRoom.isVisible })}
                  >
                    <div className={styles.togSw} />
                    <span>{selectedRoom.isVisible ? "Visible" : "Hidden"}</span>
                  </button>
                </div>
                <button className={styles.delRoom} onClick={() => deleteRoom(selectedRoom.roomId)}>🗑 Remove room</button>
              </div>
            )}
          </div>
        </div>

      </div>
      {confirmDelRoom && (() => {
        const room = rooms.find((r) => r.roomId === confirmDelRoom);
        const name = room?.name || "this room";
        return (
          <ConfirmModal
            message={`Remove "${name}"?`}
            detail="Quests inside this room will no longer be visible to kids."
            confirmLabel="Remove"
            typeToConfirm={name}
            onCancel={() => setConfirmDelRoom(null)}
            onConfirm={confirmDeleteRoom}
          />
        );
      })()}
    </div>
  );
}
